import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaUser, FaGraduationCap, FaLanguage } from 'react-icons/fa';
import { PiShootingStarFill, PiCertificateFill, PiBooksFill } from 'react-icons/pi';
import { GiJuggler } from 'react-icons/gi';
import { MdLocalActivity } from 'react-icons/md';
import { FiChevronRight } from 'react-icons/fi';
import { useTheme } from "../../../UI/Theme/ThemeContext";

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  gap: 8px;
  padding: 8px 10px 16px 10px;
  margin-bottom: 20px;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome */
  }
  border-bottom: 1px solid ${({ theme }) => theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
`;

const NavPill = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;
  border: 1px solid ${({ theme, isActive }) =>
    isActive ? '#5fbec5' : theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
  background: ${({ theme, isActive }) =>
    isActive
      ? 'linear-gradient(120deg, rgba(126, 240, 255, 0.2), rgba(62, 143, 147, 0.08))'
      : theme === 'dark' ? 'transparent' : 'transparent'};
  color: ${({ theme, isActive }) =>
    isActive ? '#5fbec5' : theme === 'dark' ? '#aaa' : '#555'};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${({ isActive }) => isActive ? '0 0 10px rgba(95, 190, 197, 0.3)' : 'none'};

  &:hover {
    border-color: #5fbec5;
    color: ${({ theme }) => theme === 'dark' ? '#fff' : '#000'};
  }
  
  svg {
    font-size: 1.1rem;
  }
`;

const NavArrow = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'};
  font-size: 1.2rem;
`;

const NAV_STEPS = [
  { id: 1, label: "Basic Info", icon: <FaUser /> },
  { id: 2, label: "Education", icon: <FaGraduationCap /> },
  { id: 3, label: "Experience", icon: <PiShootingStarFill /> },
  { id: 4, label: "Skills", icon: <GiJuggler /> },
  { id: 5, label: "Certifications", icon: <PiCertificateFill /> },
  { id: 6, label: "Publications", icon: <PiBooksFill /> },
  { id: 7, label: "Languages", icon: <FaLanguage /> },
  { id: 8, label: "Hobbies", icon: <MdLocalActivity /> },
];

const CreateStepNav = ({ step, setStep }) => {
  const { themeName } = useTheme();
  const navRef = useRef(null);

  useEffect(() => {
    if (navRef.current) {
      const activeEl = navRef.current.querySelector('[data-active="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [step]);

  return (
    <NavContainer theme={themeName} ref={navRef}>
      {NAV_STEPS.map((s, index) => {
        const isActive = s.id === step;
        return (
          <React.Fragment key={s.id}>
            <NavPill
              theme={themeName}
              isActive={isActive}
              data-active={isActive ? 'true' : 'false'}
              onClick={(e) => {
                e.preventDefault();
                setStep(s.id);
              }}
              title={s.label}
              type="button"
            >
              {s.icon}
              {s.label}
            </NavPill>
            {index < NAV_STEPS.length - 1 && (
              <NavArrow theme={themeName}>
                <FiChevronRight />
              </NavArrow>
            )}
          </React.Fragment>
        );
      })}
    </NavContainer>
  );
};

export default CreateStepNav;
